import { Card } from "@/components/ui/card";
import AddressStep from "./steps/AddressStep";
import ContactStep from "./steps/ContactStep";
import ParcelStep from "./steps/ParcelStep";
import ConfirmationStep from "./steps/ConfirmationStep";
import ProgressBar from "./ProgressBar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useParcelBookingForm } from "@/hooks/useParcelBookingForm";
import { FormNavigation } from "./parcel-form/FormNavigation";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Trash2, Save } from "lucide-react";
import { ProfileCompletionModal } from "./profile/ProfileCompletionModal";

export type ParcelDetails = {
  id?: string; // for editing existing parcels
  type: string;
  description: string;
  weight: string;
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  value: string;
  quantity: number;
  otherTypeSpecification: string;
  insuranceEnabled: boolean;
};

export type ContactDetails = {
  isMyself: boolean;
  name: string;
  phone: string;
  email: string;
  isPrimary?: boolean;
  sortOrder?: number;
};

export type BookingFormData = {
  collectionAddress: {
    street: string;
    street2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  deliveryAddress: {
    street: string;
    street2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  collectionContacts: ContactDetails[];
  deliveryContacts: ContactDetails[];
  parcels: ParcelDetails[];
  collectionDateTime: string;
  instructions: string;
  deliveryPreference: 'Cheapest' | 'Fastest';
};

const ParcelBookingForm = () => {
  const isMobile = useIsMobile();
  const {
    currentStep,
    formData,
    setFormData,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleConfirmBooking,
    showTerms,
    setShowTerms,
    isSubmitting,
    isBookingComplete,
    navigateToBookings,
    resetForm,
    showProfileModal,
    handleProfileComplete,
    isProfileUpdating,
  } = useParcelBookingForm();

  const [isAddressValid, setIsAddressValid] = useState(false);
  const [isContactsValid, setIsContactsValid] = useState(false);
  const [isParcelsValid, setIsParcelsValid] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [saveMessage, setSaveMessage] = useState("");
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());

  const isNextDisabled = currentStep === 1 && !isAddressValid || currentStep === 2 && !isContactsValid || currentStep === 3 && !isParcelsValid || isSubmitting;

  // Handle next with validation tracking
  const handleNextWithValidation = () => {
    // Mark this step as attempted
    setAttemptedSteps(prev => new Set(prev).add(currentStep));
    
    // Only proceed if valid
    if (!isNextDisabled) {
      handleNext();
    }
  };

  // Handle previous and clear attempted status
  const handlePreviousWithClear = () => {
    setAttemptedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentStep);
      return newSet;
    });
    handlePrevious();
  };

  // Update the saved status message
  useEffect(() => {
    if (!lastSavedTime) return;
    const updateSaveMessage = () => {
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - lastSavedTime.getTime()) / 1000);
      if (diffInSeconds < 5) {
        setSaveMessage("Progress saved");
      } else if (diffInSeconds < 60) {
        setSaveMessage(`Saved ${diffInSeconds} seconds ago`);
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        setSaveMessage(`Saved ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`);
      } else {
        const hours = Math.floor(diffInSeconds / 3600);
        setSaveMessage(`Saved ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`);
      }
    };
    updateSaveMessage();
    const interval = setInterval(updateSaveMessage, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [lastSavedTime]);

  // Update last saved time when form data changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLastSavedTime(new Date());
    }, 500);
    return () => clearTimeout(timer);
  }, [formData]);
  return <div className="container max-w-3xl py-4 md:px-4 md:py-8 w-full px-[8px]">
      <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 text-foreground">
        Book a Parcel Delivery
      </h1>
      
      <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 mb-6 shadow-sm border border-border">
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <ProgressBar currentStep={currentStep} />
          </div>
          
          <Button variant="outline" size="sm" className="flex items-center gap-1 whitespace-nowrap" onClick={() => {
          if (confirm("Are you sure you want to clear your progress and start over?")) {
            resetForm();
          }
        }}>
            <Trash2 className="h-4 w-4" />
            {!isMobile && <span>Reset Form</span>}
          </Button>
        </div>
      </div>
      
      <Card className="p-2 md:p-4 border shadow-sm w-full bg-card">
        <div className="px-2 md:px-4">
          {currentStep === 1 && <AddressStep formData={formData} setFormData={setFormData} onValidationChange={setIsAddressValid} />}
          {currentStep === 2 && <ContactStep formData={formData} setFormData={setFormData} onValidationChange={setIsContactsValid} />}
          {currentStep === 3 && <ParcelStep formData={formData} setFormData={setFormData} onValidationChange={setIsParcelsValid} />}
          {currentStep === 4 && <ConfirmationStep formData={formData} onConfirm={handleSubmit} showTerms={showTerms} setShowTerms={setShowTerms} isSubmitting={isSubmitting} isBookingComplete={isBookingComplete} onNavigateToBookings={navigateToBookings} />}
        </div>
        <div className="px-2 md:px-4">
          <div className="flex justify-end">
            <FormNavigation 
              currentStep={currentStep} 
              onNext={handleNextWithValidation} 
              onPrevious={handlePreviousWithClear} 
              onShowTerms={handleConfirmBooking} 
              isNextDisabled={isNextDisabled}
              showValidationError={attemptedSteps.has(currentStep) && isNextDisabled}
            />
          </div>
          
          {/* Last saved status - moved to bottom and centered */}
          {lastSavedTime && <div className="flex items-center justify-center text-xs text-muted-foreground mt-4 pt-4 border-t border-border">
              <Save className="h-3 w-3 mr-1" />
              <span>{saveMessage}</span>
            </div>}
        </div>
      </Card>
      
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onComplete={handleProfileComplete}
        isLoading={isProfileUpdating}
      />
    </div>;
};

export default ParcelBookingForm;
